import React, { useState, useEffect } from 'react';
import { useRouter } from '../../../components';
import { useGet } from '../../../components';
import { Item } from '../../items/item';
import { NotFound } from '../../../components';
import env from '../../../env';
import { useStore } from '../../../store';
import { ErrorPopup, Loading } from '../../../components';
import { useInstagram } from '../../../components';
import { 
  MetaTag, 
  BreadcrumbSchema, 
  OrganizationSchema,
  AboutPageSchema,
  ContactPageSchema,
  WebPageSchema
} from '../../../components';
import cmsUtils from '../../utils/cmsUtils';
import utils from '../../../utils';
import { gtm } from '../../../lib/tracking';

const log = (...args) => {
  //console.log(args);
}
const areEqual = (prevProps, nextProps) => {
  return (prevProps.pageData === nextProps.pageData)
};
const PageMemo = React.memo(Page, areEqual)

export function ViewPage() {
  const { dispatch } = useStore();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [data, setData] = useState(null);
  //const path = usePath();
  //log('view page', path);
  const { pathname, Redirect } = useRouter();
  useInstagram();

  const get = useGet();
  log('0. viewpage ready');

  useEffect(() => {
    log('*. start loading')
    setPageLoaded(false);
    get.send(env.apiBase + "/api/cms/GetPublishedPage?path=" + pathname);
    // eslint-disable-next-line
  }, [pathname]);

  useEffect(() => {
    if (data){
      dispatch({ type: 'SET_PAGE', payload: data.data })
      dispatch({ type: 'SET_MENU', payload: data.menu })
    }
    // eslint-disable-next-line
  }, [data]);

  if (get.loading()) {
    return <Loading />
  } else if (get.hasErrors()) {
    return <ErrorPopup title="Loading Error" errors={get.errors} />
  }

  //log(router.pathname);
  //log(get.loading());
  log('1. viewpage component', pageLoaded, get.response);


  const pageData = get.response.data;
  //const menuData = get.response.menu;
  const redirectTo = get.response.redirectTo;

  const cmsOption = {};
  log(pageLoaded, !pageLoaded && !!pageData)
  if (!pageLoaded && pageData) {
    //log(pageLoaded, pageData, menuData)
    log('2. dispatching page & menu')
    setPageLoaded(true);
    setData(get.response);
    document.title = pageData.pageTitle;
    gtm.pageView(pathname, pageData.pageTitle);
    return null;
  } else if (redirectTo) {
    if (redirectTo.indexOf('http') >= 0 && typeof window !== `undefined`) {
      return window.location.href = redirectTo;
    } else {
      return <Redirect to={redirectTo} />
    }
  } else if (!pageLoaded && !pageData) {
    setPageLoaded(true);
    setData(get.response);
    return (<NotFound />)
  }

  log('3. call rendering memo')
  if (pageData){
    let themeColor = cmsUtils.payload(pageData, 'ColorTheme', 'magenta');
    let _hideBlogTitle = cmsUtils.payload(pageData, 'HideBlogTitle', 'false');
    let _class = document.querySelector('div.body-content').className.replace(/theme-color-.*/g, '');
    document.querySelector('div.body-content').className= _class + "theme-color-" + themeColor;

    document.querySelector('div.body-content').classList.remove("hide-blog-title-true");
    if(_hideBlogTitle === 'true'){
      document.querySelector('div.body-content').classList.add("hide-blog-title-true");
    }

    return <PageMemo pageData={pageData} cmsOption={cmsOption} />;
  }
  else
    return <NotFound />
}


function Page(props) {
  const pageData = props.pageData;
  const cmsOption = props.cmsOption;
  cmsOption.pageData = pageData;
  // document.title = pageData.pageTitle;
  log('4. page rendering', pageData, cmsOption)
  
  // Detect page type based on URL
  const pageUrl = pageData.pageUrl ? pageData.pageUrl.toLowerCase() : '';
  const isHomepage = pageUrl === '/';
  const isAboutPage = pageUrl === '/about' || 
                      pageUrl === '/about-us' || 
                      pageUrl.includes('/about');
  const isContactPage = pageUrl === '/contact' || 
                        pageUrl === '/contact-us' || 
                        pageUrl.includes('/contact');
  
  // Get page metadata
  const pageTitle = pageData.pageTitle ? `${pageData.pageTitle} | ${env.siteName}` : env.siteName;
  const pageDescription = cmsUtils.payload(pageData, 'Description') || pageData.pageTitle;
  const pageImage = cmsUtils.payload(pageData, 'ImageUrl');
  
  // Build breadcrumb data for CMS pages (if page has parent pages in hierarchy)
  const breadcrumbData = React.useMemo(() => {
    // Check if page has breadcrumb data from CMS
    if (pageData.breadcrumb && pageData.breadcrumb.items) {
      return pageData.breadcrumb;
    }
    
    // Homepage doesn't need breadcrumb schema
    if (isHomepage) {
      return null;
    }
    
    // Build basic breadcrumb: Home -> Current Page
    // This can be enhanced to include parent pages if that data becomes available
    return {
      items: [
        { title: 'Home', link: '/' },
        { title: pageData.pageTitle, link: null } // Current page
      ]
    };
  }, [pageData.breadcrumb, pageData.pageUrl, pageData.pageTitle, isHomepage]);
  
  const sectionItems = pageData.sections.map((section, index) => {
    return (
      <Section item={section} key={section.sectionName + index} cmsOption={cmsOption}></Section>
    )
  }
  );
  
  return (
    <>
      <MetaTag data={getMetaTag(pageData)} />
      
      {/* Page-specific schemas based on page type */}
      {isAboutPage && (
        <>
          {/* OrganizationSchema for About page */}
          <OrganizationSchema />
          {/* AboutPageSchema wraps the page */}
          <AboutPageSchema
            name={pageTitle}
            description={pageDescription}
            url={pageData.pageUrl}
            breadcrumb={breadcrumbData}
            image={pageImage}
          />
        </>
      )}
      
      {isContactPage && (
        <>
          {/* ContactPageSchema for Contact page */}
          <ContactPageSchema
            name={pageTitle}
            description={pageDescription}
            url={pageData.pageUrl}
            breadcrumb={breadcrumbData}
            image={pageImage}
          />
        </>
      )}
      
      {!isHomepage && !isAboutPage && !isContactPage && (
        <>
          {/* Generic WebPageSchema for other pages */}
          <WebPageSchema
            name={pageTitle}
            description={pageDescription}
            url={pageData.pageUrl}
            breadcrumb={breadcrumbData}
            image={pageImage}
          />
        </>
      )}
      
      {/* BreadcrumbSchema for SEO - only for non-homepage pages */}
      {breadcrumbData && breadcrumbData.items && breadcrumbData.items.length > 0 && (
        <BreadcrumbSchema breadcrumb={breadcrumbData} />
      )}
      
      <div className={`cmsPage-view-mode cmsPage cmsPage--${pageData.pageTypeCode}`}>
        {sectionItems}
      </div>
    </>
  );
}

function Section(props) {
  log('5. section rendering')
  const item = props.item;
  //const sectionName = item.sectionName;
  const subItems = item.items || [];
  const cmsOption = props.cmsOption || {};
  if (!subItems || subItems.length === 0)
    return (<></>);

  const subItemsElts = subItems.map((subItem, index) =>
    <Item item={subItem} key={subItem.itemId} cmsOption={cmsOption}></Item>
  );
  return (
    <>
      {subItemsElts}
    </>
  );
}

function getMetaTag(pageData) {
  //const country = utils.site.isNZ? 'New Zealand' : 'Australia'
  const country = "Australia";
  const siteName = env.siteName;
  const titleSuffix = env.titleSuffix;

  return {
    title: pageData.pageTitle + ' | ' + titleSuffix,
    description: cmsUtils.payload(pageData, 'Description'),
    keywords: cmsUtils.payload(pageData, 'Keyword'),
    heroImageUrl: utils.site.fullUrl(cmsUtils.payload(pageData, 'ImageUrl')),
    language: 'English',
    country: country,
    siteName: siteName,
    url: utils.site.fullUrl(pageData.pageUrl),
    //canonical: utils.site.resourcePath(pageData.pageUrl)
  }
}